import { type OrgDepartment, type OrgStructure, useDepartmentMembers } from '../../api/org';
import { Avatar } from '../../components/Avatar';
import { Icon } from '../../components/Icon';
import { ErrorState, LoadingState } from '../../components/states';
import { usePersonDrawer } from '../../layout/shellContext';

interface Props {
  structure: OrgStructure;
  department: OrgDepartment;
  path: OrgDepartment[];
  onSelect: (id: string) => void;
}

/** Выбранное подразделение: путь, числа, руководители и сотрудники напрямую. */
export function UnitPanel({ structure, department, path, onSelect }: Props) {
  const { open } = usePersonDrawer();
  const members = useDepartmentMembers(department.id);
  const headIds = new Set(department.heads.map((head) => head.id));
  const direct = (members.data?.items ?? []).filter((member) => !headIds.has(member.id));

  return (
    <aside className="panel org-detail" aria-label="Выбранное подразделение">
      <div>
        {path.length > 1 ? (
          <nav className="org-path" aria-label="Путь">
            {path.slice(0, -1).map((item) => (
              <span key={item.id} style={{ display: 'contents' }}>
                <button type="button" onClick={() => onSelect(item.id)}>
                  {item.name}
                </button>
                <span className="sep">
                  <Icon name="chevronRight" size={12} />
                </span>
              </span>
            ))}
          </nav>
        ) : null}
        <h2>{department.name}</h2>
      </div>

      <div className="mini-stats">
        <div>
          <strong>{department.totalCount}</strong>
          <span>всего</span>
        </div>
        <div>
          <strong>{department.directCount}</strong>
          <span>напрямую</span>
        </div>
        <div>
          <strong>{department.childIds.length}</strong>
          <span>отделов внутри</span>
        </div>
      </div>

      <div className="detail-block">
        <h3 className="section-title">{department.heads.length > 1 ? 'Руководители' : 'Руководитель'}</h3>
        {department.heads.length ? (
          <div className="member-list">
            {department.heads.map((head) => {
              const person = structure.people[head.id];
              if (!person) return null;
              return (
                <button key={head.id} type="button" className="member" onClick={() => open(person.id)}>
                  <Avatar name={person.fullName} photoUrl={person.photoUrl} size="md" />
                  <span className="member-text">
                    <b>{person.fullName}</b>
                    <small>
                      {person.position ?? 'Руководитель'}
                      {head.coHead ? ' · соруководитель' : ''}
                    </small>
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="notice">
            <Icon name="alert" />
            <span>Руководитель не назначен — сотрудники подчиняются руководителю уровнем выше.</span>
          </div>
        )}
      </div>

      <div className="detail-block">
        <h3 className="section-title">Сотрудники · {members.data ? direct.length : '…'}</h3>
        {members.isPending ? <LoadingState label="Загружаю сотрудников" /> : null}
        {members.isError ? <ErrorState error={members.error} onRetry={() => void members.refetch()} /> : null}
        {members.data && !direct.length ? <span className="muted">Напрямую никого нет</span> : null}
        <div className="member-list">
          {direct.map((member) => (
            <button key={member.id} type="button" className="member" onClick={() => open(member.id)}>
              <Avatar name={member.fullName} photoUrl={member.photoUrl} size="md" />
              <span className="member-text">
                <b>{member.fullName}</b>
                <small>{member.position ?? 'Должность не указана'}</small>
              </span>
              {member.departmentsCount > 1 ? (
                <span className="chip chip-violet">{member.departmentsCount} отд.</span>
              ) : null}
            </button>
          ))}
        </div>
      </div>
    </aside>
  );
}
